/* Deprecated - please use @hivebrite/design-system/Inputs */

import { trim } from 'lodash';
import PropTypes from 'prop-types';
import {
  createElement,
  forwardRef,
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { focusOutline } from '@hivebrite/design-system/Theme';
import { usePrevious } from '@hivebrite/react-hooks';

import Required from 'hb-react/shared/components/Required';
import IconExclamationMarkHollow from 'hb-react/shared/icons/ExclamationMarkHollow';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';

const ICON_SIZE = 20;

const CloseIcon = () => (
  <FontAwesomeIcon height="16px" iconName="xmark" iconType="solid" />
);

const InputComponent = forwardRef(
  (
    {
      children,
      clearable,
      dataTestId,
      fontSize,
      lineHeight,
      options,
      type,
      value,
      size,
      hasIcon,
      ...otherProps
    },
    ref,
  ) => {
    switch (type) {
      case 'textarea':
        return (
          <StyledInput
            as="textarea"
            data-testid={dataTestId}
            ref={ref}
            value={value}
            {...otherProps}
          />
        );

      default:
        return (
          <StyledInput
            clearable={clearable}
            data-testid={dataTestId}
            hasIcon={hasIcon}
            ref={ref}
            size={size}
            type={type}
            value={value}
            {...otherProps}
          />
        );
    }
  },
);

export const Textarea = forwardRef(({ icon, ...otherProps }, ref) => (
  <Input ref={ref} type="textarea" role="textbox" {...otherProps} />
));

export const CKEditorTextarea = forwardRef(
  (
    { icon, name, width, toolbar, onChange, value, editorProps, ...otherProps },
    ref,
  ) => {
    const [prevContent, setPrevContent] = useState(value);

    useEffect(() => {
      if (!('CKEDITOR' in window)) {
        return;
      }

      const instance = window.CKEDITOR.replace(name, {
        width: width ?? '100%',
        toolbar,
        ...editorProps,
      });

      instance.on('unlockSnapshot', () => {
        const content = instance.getData();

        if (onChange && prevContent !== content) {
          setPrevContent(content);
          onChange({ target: { value: content } });
        }
      });

      return () => {
        instance.destroy();
      };
    }, [name]);

    useEffect(() => {
      if (!('CKEDITOR' in window)) {
        return;
      }

      const instance = window.CKEDITOR.instances?.[name];

      if (instance && value !== instance.getData()) {
        instance.setData(value);
      }
    }, [value, name]);

    return (
      <Input
        width={width}
        ref={ref}
        type="textarea"
        role="textbox"
        name={name}
        onChange={onChange}
        value={value}
        {...otherProps}
      />
    );
  },
);

/**
 * @deprecated please use `@hivebrite/design-system/Inputs` instead
 */
const Input = forwardRef(
  (
    {
      autoFocus,
      className,
      clearable,
      disabled,
      error,
      fontSize,
      height,
      hideLabel,
      icon,
      iconSpaceRight,
      id,
      label,
      labelColor,
      labelFontWeight,
      labelFontSize,
      lineHeight = 1.2,
      onBlur,
      onChange,
      onFocus,
      required,
      size = 'normal',
      type = 'text',
      value,
      width,
      ...otherProps
    },
    ref,
  ) => {
    const [isFocus, setIsFocus] = useState(false);

    const inputRef = useRef();
    const prevFocus = usePrevious(isFocus);

    const isClearable = useMemo(
      () => clearable && value && trim(value) !== '',
      [clearable, value],
    );
    const hasError = !!error;
    const errorId = `${id}-error`;

    const handleFocus = useCallback(() => {
      setIsFocus(true);

      if (onFocus) {
        onFocus();
      }
    }, [onFocus]);
    const handleBlur = useCallback(() => {
      setIsFocus(false);

      if (onBlur) {
        onBlur();
      }
    }, [onBlur]);
    const handleClearValue = useCallback(() => {
      if (isClearable) {
        const event = new Event('change', { bubbles: true });

        inputRef.current.value = '';
        inputRef.current.dispatchEvent(event);

        if (onChange) {
          onChange(event);
        }

        inputRef.current.focus();
      }
    }, [onChange, isClearable]);

    useEffect(() => {
      if (autoFocus) {
        inputRef.current.focus();
      }
    }, [autoFocus]);

    useEffect(
      () => () => {
        if (!isFocus && prevFocus && onBlur) {
          onBlur();
        }
      },
      [],
    );

    return (
      <Wrapper className={className} width={width}>
        <LabelWrapper hideLabel={hideLabel}>
          <Label
            color={labelColor}
            fontWeight={labelFontWeight}
            fontSize={labelFontSize}
            htmlFor={id}
          >
            {label}
            {required && <Required />}
          </Label>
        </LabelWrapper>
        <InputWrapper
          hasError={hasError}
          height={height}
          isDisabled={disabled}
          isFocus={isFocus}
          width={width}
        >
          <InputComponent
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError}
            disabled={disabled}
            fontSize={fontSize}
            hasIcon={isClearable || !!icon}
            id={id}
            lineHeight={lineHeight}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={handleFocus}
            ref={mergeRefs([ref, inputRef])}
            size={size}
            type={type}
            value={value}
            {...otherProps}
          />

          {(isClearable || icon) && (
            <IconWrapper
              iconSpaceRight={iconSpaceRight}
              isClearable={isClearable}
              onClick={handleClearValue}
              onKeyPress={onEnterPress(handleClearValue)}
              tabIndex={isClearable ? 0 : -1}
            >
              {createElement(isClearable ? CloseIcon : icon, {
                height: '100%',
                width: '100%',
                // @hivebrite/design-system icons use `size` props instead of the `width` and `height` ones.
                size: '100%',
              })}
            </IconWrapper>
          )}
        </InputWrapper>
        {hasError && (
          <WrapperError id={errorId}>
            <IconExclamationMarkHollow height={ICON_SIZE} width={ICON_SIZE} />
            <Box ml={2} color="inherit">
              {error}
            </Box>
          </WrapperError>
        )}
      </Wrapper>
    );
  },
);

Input.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

export default Input;

const getPadding = ({ theme, size }) => {
  switch (size) {
    case 'small':
      return css`
        ${theme.space[2] - 1}px
        ${theme.space[3] - 1}px !important;
      `;

    case 'extra-small':
      return css`
        ${theme.space[1]}px ${theme.space[2]}px !important;
      `;

    case 'normal':
    default:
      return css`
        ${theme.space[3] - 1}px !important;
      `;
  }
};

const getBoxShadow = ({ theme, hasError, isFocus }) => {
  if (isFocus) {
    return `${theme.input.focus.borderColor} 0 0 0 1px inset !important`;
  }

  if (hasError) {
    return `${theme.input.error.color} 0 0 0 1px inset !important`;
  }

  return `${theme.input.borderColor} 0 0 0 1px inset !important`;
};

const getInputStyle = ({ fontSize, theme }) => css`
  background: transparent !important;
  border-radius: 0 !important;
  border: 0 !important;
  color: ${theme.input.color} !important;
  font-family: 'Inter', 'Nunito', Helvetica, Arial, sans-serif !important;
  font-size: ${fontSize || theme.fontSizes[2]} !important;
  padding: ${getPadding};
  width: 100%;

  ${(props) =>
    props.hasIcon &&
    css`
      padding-right: ${props.theme.space[3] * 2 + ICON_SIZE}px !important;
    `}

  &:focus {
    outline: none !important;
  }
`;

const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
}))``;

const InputWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['isFocus', 'hasError', 'isDisabled'].includes(prop),
  })
  .attrs(({ isDisabled }) => ({
    bg: isDisabled ? 'dove' : 'white',
  }))`
  border-radius: 3px;
  box-shadow: ${getBoxShadow};
  position: relative;
`;

const LabelWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['hideLabel'].includes(prop),
  })
  .attrs(() => ({
    mb: 2,
  }))`
  ${(props) =>
    props.hideLabel &&
    css`
      border: 0px !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(100%) !important;
      height: 1px !important;
      overflow: hidden !important;
      padding: 0px !important;
      position: absolute !important;
      white-space: nowrap !important;
      width: 1px !important;
    `}
`;

export const Label = styled.label`
  color: ${({ color, theme }) => color || theme.input.color};
  cursor: ${(props) => (props.htmlFor ? 'pointer' : 'default')} !important;
  font-size: ${({ fontSize, theme }) => fontSize || theme.fontSizes[2]};
  font-weight: ${({ fontWeight, theme }) => fontWeight || theme.fontWeights[1]};
  user-select: none;
`;

const StyledInput = styled.input`
  ${getInputStyle};

  height: 100% !important;
  resize: none;

  &::placeholder {
    color: ${(props) => props.theme.input.placeholder} !important;
  }

  &:-webkit-autofill {
    -webkit-text-fill-color: ${(props) => props.theme.input.color} !important;
    transition: background-color 5000s ease-in-out 0s !important;
  }
`;

const WrapperError = styled(Box).attrs(() => ({
  alignItems: 'center',
  mt: 2,
}))`
  color: ${(props) => props.theme.input.error.color};
`;

const IconWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['iconSpaceRight', 'isClearable'].includes(prop),
  })
  .attrs(() => ({
    height: ICON_SIZE,
    width: ICON_SIZE,
  }))`
  ${focusOutline}

  color: ${(props) => props.theme.input.focus.borderColor};
  cursor: ${(props) => (props.isClearable ? 'pointer' : 'default')};
  margin-top: -${ICON_SIZE / 2}px;
  pointer-events: ${(props) => (props.isClearable ? 'all' : 'none')};
  position: absolute;
  right: ${(props) => props.iconSpaceRight || props.theme.space[3]}px;
  top: 50%;
`;
